import { Stack, Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { CSSProperties } from 'react';
import type { DraggingStyle, NotDraggingStyle } from 'react-beautiful-dnd';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { QuestionBankQuestion } from './QuestionBankQuestion';
import { QUESTIONS_BY_CATEGORY } from './constants';
import type { QuestionBankQuestion as Question } from './constants';

type QuestionWithId = Question & { id: string };

const setDefaults = (question: Question, id: string): QuestionWithId => {
  return {
    id,
    next: 'byOrder',
    ...question,
  };
};

const getAllQuestions = (): QuestionWithId[] => {
  return QUESTIONS_BY_CATEGORY.flatMap((category) => category.questions).map((question, index) =>
    setDefaults(question, `bankid_${index}`),
  );
};

export const ALL_QUESTIONS = getAllQuestions();

const QuestionWrapper = styled.div(
  ({ isDragging }: { isDragging: boolean }) => `
  ${
    isDragging &&
    `
    cursor: grabbing;
    `
  }
`,
);

const CenteredText = styled(Text)`
  text-align: center;
`;

const ScrollingStack = styled(Stack)`
  overflow-y: auto;
  padding-bottom: ${tokens.spacer.x3};
`;

const Container = styled(Stack)`
  overflow: hidden;
`;

const DraggableQuestion = ({
  question,
  isDragging,
}: {
  question: QuestionWithId;
  isDragging: boolean;
}) => {
  return (
    <QuestionWrapper isDragging={isDragging}>
      <QuestionBankQuestion type={question.type} text={question.text} />
    </QuestionWrapper>
  );
};

const getItemStyle = (
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
): CSSProperties => ({
  userSelect: 'none',
  ...draggableStyle,
});

export const QuestionBank = (): JSX.Element => {
  const { t } = useTranslation('feedback');
  return (
    <Container direction="column" spacing="x4">
      <CenteredText textType="subtitle2">
        {t('survey-wizard.preview.question-bank.description')}
      </CenteredText>

      <Droppable droppableId="question-bank" type="question-bank" isDropDisabled>
        {(droppableProvided) => (
          <ScrollingStack
            direction="column"
            spacing="x2"
            {...droppableProvided.droppableProps}
            ref={droppableProvided.innerRef}
          >
            {ALL_QUESTIONS.map((question, index) => (
              <Draggable key={question.id} draggableId={question.id} index={index}>
                {(draggableProvided, snapshot) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    {...draggableProvided.dragHandleProps}
                    style={getItemStyle(draggableProvided.draggableProps.style)}
                  >
                    <DraggableQuestion isDragging={snapshot.isDragging} question={question} />
                  </div>
                )}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
          </ScrollingStack>
        )}
      </Droppable>
    </Container>
  );
};
